import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Loader2, ArrowLeft, ArrowRight, Camera, Download, Share2, MapPin, Phone, Mail, CheckCircle2, FileText } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import LanguageToggle from '@/components/common/LanguageToggle';
import { useUiLanguage } from '@/lib/i18n';
import useStore from '@/store/useStore';
import { apiMerchantSetup, apiMerchantTheme } from '@/services/api';
import { getMerchantPrefs, setMerchantPrefs } from '@/lib/merchantPrefs';

const CUISINES = ['American', 'Italian', 'Mexican', 'Japanese', 'Chinese', 'Indian', 'Mediterranean'];
const TOTAL_STEPS = 4;
const ONBOARDING_PRIMARY = '#0E7A62';

export default function MerchantOnboardingPage() {
  const navigate = useNavigate();
  const { t } = useUiLanguage();
  const { merchantToken, merchantUser, setMerchantUser, setMerchantBrandColor } = useStore();
  const merchantId = merchantUser?.id || 'default';
  const logoFileRef = useRef(null);
  const menuFileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [draft, setDraft] = useState({
    restaurant_name: '',
    cuisine_types: [],
    custom_cuisine: '',
    logo_base64: '',
    menu_pdf_name: '',
    menu_pdf_base64: '',
    address_line: '',
    city: '',
    state: '',
    zip: '',
    google_place_query: '',
    opening_time: '10:00:00',
    closing_time: '12:00:00',
    contact_phone: '',
    contact_email: '',
  });

  useEffect(() => {
    if (!merchantToken) {
      navigate('/merchant', { replace: true });
      return;
    }
    if (merchantUser?.onboarding_complete) {
      navigate('/merchant/dashboard', { replace: true });
      return;
    }
    const prefs = getMerchantPrefs(merchantId);
    const savedDraft = prefs.onboardingDraft || {};
    const fromMerchant = {
      restaurant_name: merchantUser?.restaurant_name || '',
      logo_base64: merchantUser?.logo_base64 || '',
    };
    setDraft((prev) => ({ ...prev, ...fromMerchant, ...savedDraft }));
    if (prefs.onboardingStep && Number.isFinite(prefs.onboardingStep)) {
      setStep(Math.min(Math.max(Number(prefs.onboardingStep), 1), TOTAL_STEPS));
    }
    if (!merchantUser?.brand_color || merchantUser?.brand_color === 'black') {
      setMerchantBrandColor('#0E7A62');
    }
  }, [merchantId, merchantToken, merchantUser, navigate, setMerchantBrandColor]);

  useEffect(() => {
    setMerchantPrefs(merchantId, { onboardingDraft: draft, onboardingStep: step });
  }, [draft, merchantId, step]);

  const logoPreview = draft.logo_base64 || '/assets/flyback.png';
  const locationString = [draft.address_line, draft.city, draft.state, draft.zip].filter(Boolean).join(', ');
  const storeUrl = typeof window !== 'undefined'
    ? `${(process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin).replace(/\/+$/, '')}/store/${merchantUser?.id || 'demo'}`
    : '';

  const onDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mime = String(file.type || '').toLowerCase();
    if (file.size > 8 * 1024 * 1024) {
      setError(t('Please upload file under 8MB.'));
      return;
    }
    if (mime && !['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif', 'image/webp'].includes(mime)) {
      setError(t('Please upload a PNG, JPG, SVG, GIF, or WEBP logo.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      onDraft({ logo_base64: String(ev.target?.result || '') });
    };
    reader.readAsDataURL(file);
  };

  const onMenuFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mime = String(file.type || '').toLowerCase();
    if (file.size > 12 * 1024 * 1024) {
      setError(t('Please upload menu PDF under 12MB.'));
      return;
    }
    if (mime && mime !== 'application/pdf') {
      setError(t('Please upload menu in PDF format.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      onDraft({
        menu_pdf_name: file.name,
        menu_pdf_base64: String(ev.target?.result || ''),
      });
      setStatusMessage(t('Menu PDF uploaded for onboarding.'));
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const toggleCuisine = (cuisine) => {
    setDraft((prev) => {
      const selected = prev.cuisine_types || [];
      const exists = selected.includes(cuisine);
      if (exists) return { ...prev, cuisine_types: selected.filter((x) => x !== cuisine) };
      return { ...prev, cuisine_types: [...selected, cuisine] };
    });
  };

  const goNext = () => {
    setError('');
    if (step === 1 && !draft.restaurant_name.trim()) {
      setError(t('Restaurant name is required.'));
      return;
    }
    if (step === 2 && (!draft.address_line.trim() || !draft.city.trim() || !draft.state.trim() || !draft.zip.trim())) {
      setError(t('Please fill address line, city, state, and ZIP code.'));
      return;
    }
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const downloadQrCard = async () => {
    const node = document.getElementById('onboarding-qr-card');
    if (!node) return;
    const qrCanvas = node.querySelector('canvas');
    if (!qrCanvas) {
      setError(t('Unable to render QR right now. Please try again.'));
      return;
    }

    try {
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = 1080;
      exportCanvas.height = 1480;
      const ctx = exportCanvas.getContext('2d');
      if (!ctx) return;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

      ctx.strokeStyle = '#e7e5e4';
      ctx.lineWidth = 4;
      ctx.strokeRect(36, 36, exportCanvas.width - 72, exportCanvas.height - 72);

      const restaurantLogo = await loadImage(logoPreview);
      ctx.drawImage(restaurantLogo, 120, 120, 120, 120);

      ctx.fillStyle = '#9ca3af';
      ctx.font = '500 36px "Bricolage Grotesque", sans-serif';
      ctx.fillText('RESTAURANT', 280, 170);
      ctx.fillStyle = '#111827';
      ctx.font = '700 56px "Bricolage Grotesque", sans-serif';
      ctx.fillText((draft.restaurant_name || 'Your Restaurant').slice(0, 24), 280, 248);

      const qrBoxX = 130;
      const qrBoxY = 320;
      const qrBoxSize = 820;
      ctx.strokeStyle = '#d6d3d1';
      ctx.lineWidth = 3;
      ctx.strokeRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(qrBoxX + 16, qrBoxY + 16, qrBoxSize - 32, qrBoxSize - 32);
      ctx.drawImage(qrCanvas, qrBoxX + 36, qrBoxY + 36, qrBoxSize - 72, qrBoxSize - 72);

      const flybackLogo = await loadImage('/assets/flyback.png');

      ctx.fillStyle = '#111827';
      ctx.font = '700 56px "Bricolage Grotesque", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Scan. Dine. Earn Rewards.', exportCanvas.width / 2, 1200);
      ctx.fillStyle = '#78716c';
      ctx.font = '500 46px "Bricolage Grotesque", sans-serif';
      ctx.fillText('Join now and unlock exclusive points and perks.', exportCanvas.width / 2, 1260);

      ctx.drawImage(flybackLogo, (exportCanvas.width - 70) / 2, 1276, 70, 70);
      ctx.fillStyle = '#9ca3af';
      ctx.font = '500 36px "Bricolage Grotesque", sans-serif';
      ctx.fillText('Powered by FlyBack', exportCanvas.width / 2, 1365);

      const a = document.createElement('a');
      a.href = exportCanvas.toDataURL('image/png');
      a.download = `${(draft.restaurant_name || 'flyback').replace(/\s+/g, '-').toLowerCase()}-store-qr.png`;
      a.click();
      setStatusMessage(t('Branded QR image downloaded.'));
      setError('');
    } catch (e) {
      setError(t('Unable to download QR image right now.'));
    }
  };

  const shareLink = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      setStatusMessage(t('Store link copied. You are ready to share.'));
      setError('');
    } catch {
      setError(t('Unable to copy link right now.'));
    }
  };

  const finish = async () => {
    setLoading(true);
    setError('');
    try {
      await apiMerchantSetup(draft.restaurant_name.trim(), locationString, draft.logo_base64 || null);
      const nextBrandColor = merchantUser?.brand_color && merchantUser?.brand_color !== 'black'
        ? merchantUser.brand_color
        : ONBOARDING_PRIMARY;
      await apiMerchantTheme(nextBrandColor, null).catch(() => {});
      setMerchantUser({
        ...merchantUser,
        restaurant_name: draft.restaurant_name.trim(),
        logo_base64: draft.logo_base64 || '',
        location: locationString,
        onboarding_complete: true,
        brand_color: nextBrandColor,
      });
      setMerchantBrandColor(nextBrandColor);
      setMerchantPrefs(merchantId, {
        onboardingDraft: null,
        onboardingStep: null,
        ai_name: merchantUser?.name || 'Flyback Advisor',
        cuisine_types: [...draft.cuisine_types, draft.custom_cuisine].filter(Boolean),
      });
      navigate('/merchant/dashboard', { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('Unable to finish setup right now.'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#f7f7f4] px-6 py-8" data-testid="merchant-onboarding">
      <div className="mx-auto max-w-[920px]">
        <div className="mb-4 flex justify-end">
          <LanguageToggle />
        </div>

        <div className="mb-4 flex items-center justify-between gap-3">
          <button onClick={goBack} disabled={step === 1} className="h-9 w-9 rounded-full border border-stone-200 bg-white flex items-center justify-center disabled:opacity-50" data-testid="onboarding-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 rounded-xl border border-stone-200 bg-white px-3 py-2">
            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider">
              {['Step 1', 'Step 2', 'Step 3', 'Complete'].map((item, idx) => {
                const itemStep = idx + 1;
                const done = step > itemStep;
                const active = step === itemStep;
                return (
                  <span key={item} className={done || active ? 'text-brand' : 'text-stone-400'}>
                    {t(item)}
                  </span>
                );
              })}
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(Math.max(step, 1) / TOTAL_STEPS) * 100}%`,
                  backgroundColor: ONBOARDING_PRIMARY,
                }}
              />
            </div>
          </div>
          <div className="w-9" />
        </div>

        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div>
              <p className="text-xs font-bold tracking-widest text-violet-500 uppercase">{t('Step 1 of 4')}</p>
              <h1 className="text-5xl font-bold mt-2 text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t("Let's set up your restaurant.")}</h1>
              <p className="text-stone-500 mt-1">{t('Simple setup for restaurant profile, branding, and guest onboarding QR.')}</p>
            </div>
            <div className="space-y-3">
              <label className="text-sm font-semibold">{t('Restaurant Name')}</label>
              <Input value={draft.restaurant_name} onChange={(e) => onDraft({ restaurant_name: e.target.value })} placeholder={t('e.g. The Daily Grind')} className="h-12 rounded-xl border-blue-300 bg-white" data-testid="restaurant-name-input" />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><label className="font-semibold">{t('Cuisine Type')}</label><span className="text-stone-400">{t('Select all that apply')}</span></div>
              <div className="flex flex-wrap gap-2">
                {CUISINES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCuisine(c)}
                    className={`px-4 h-9 rounded-full border text-sm font-medium ${draft.cuisine_types.includes(c) ? 'text-white' : 'bg-white border-stone-200 text-stone-600'}`}
                    style={draft.cuisine_types.includes(c) ? { backgroundColor: ONBOARDING_PRIMARY, borderColor: ONBOARDING_PRIMARY } : {}}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <Input
                value={draft.custom_cuisine}
                onChange={(e) => onDraft({ custom_cuisine: e.target.value })}
                placeholder={t('Add custom cuisine (optional)')}
                className="h-10 rounded-xl bg-white"
              />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><label className="font-semibold">{t('Restaurant Logo')}</label><span className="text-stone-400">{t('Optional')}</span></div>
              <input type="file" ref={logoFileRef} accept="image/*" className="hidden" onChange={onLogoFile} data-testid="logo-file-input" />
              <button type="button" onClick={() => logoFileRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-stone-200 p-8 bg-white hover:bg-stone-50 transition-colors">
                <div className="mx-auto w-fit h-12 w-12 rounded-full bg-stone-100 flex items-center justify-center"><Camera className="w-5 h-5" /></div>
                <p className="mt-3 font-semibold">{draft.logo_base64 ? t('Logo uploaded') : t('Click to upload or drag and drop')}</p>
                <p className="text-xs text-stone-400 mt-1">{t('SVG, PNG, JPG, GIF or WEBP (max 800x400px)')}</p>
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><label className="font-semibold">{t('Menu Upload (PDF)')}</label><span className="text-stone-400">{t('Optional')}</span></div>
              <input type="file" ref={menuFileRef} accept="application/pdf" className="hidden" onChange={onMenuFile} />
              <button type="button" onClick={() => menuFileRef.current?.click()} className="w-full h-12 rounded-xl border border-stone-200 bg-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-stone-50">
                <FileText className="w-4 h-4" /> {draft.menu_pdf_name ? `${t('Uploaded')}: ${draft.menu_pdf_name}` : t('Upload Menu PDF')}
              </button>
            </div>
            <button onClick={goNext} className="w-full h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: ONBOARDING_PRIMARY }} data-testid="onboarding-next-step1">
              {t('Next Step')} <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div>
              <p className="text-xs font-bold tracking-widest text-violet-500 uppercase">{t('Step 2 of 4')}</p>
              <h1 className="text-5xl font-bold mt-2 text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t('Where are you located?')}</h1>
              <p className="text-stone-500 mt-2">{t('Help customers find your restaurant and enable location-based rewards.')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-semibold flex items-center gap-2">
                  {t('Google Places')}
                  <span className="h-5 px-2 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider inline-flex items-center">{t('Coming Soon')}</span>
                </label>
                <Input
                  value={draft.google_place_query}
                  onChange={(e) => onDraft({ google_place_query: e.target.value })}
                  placeholder={t('Search via Google Places (coming soon)')}
                  className="h-11 rounded-xl bg-stone-50"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-semibold">{t('Restaurant Address')}</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                  <Input value={draft.address_line} onChange={(e) => onDraft({ address_line: e.target.value })} placeholder={t('Start typing your address...')} className="h-12 rounded-xl border-blue-300 bg-white pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold">{t('Opening Time (HH:mm:ss)')}</label>
                <Input value={draft.opening_time} onChange={(e) => onDraft({ opening_time: e.target.value })} className="h-11 rounded-xl bg-white" placeholder="10:00:00" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold">{t('Closing Time (HH:mm:ss)')}</label>
                <Input value={draft.closing_time} onChange={(e) => onDraft({ closing_time: e.target.value })} className="h-11 rounded-xl bg-white" placeholder="12:00:00" />
              </div>
              <div className="space-y-2"><label className="text-sm font-semibold">{t('City')}</label><Input value={draft.city} onChange={(e) => onDraft({ city: e.target.value })} className="h-11 rounded-xl bg-white" /></div>
              <div className="space-y-2"><label className="text-sm font-semibold">{t('State')}</label><Input value={draft.state} onChange={(e) => onDraft({ state: e.target.value })} className="h-11 rounded-xl bg-white" /></div>
              <div className="space-y-2"><label className="text-sm font-semibold">{t('ZIP Code')}</label><Input value={draft.zip} onChange={(e) => onDraft({ zip: e.target.value })} className="h-11 rounded-xl bg-white" /></div>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5 space-y-3">
              <h3 className="font-semibold">{t('Contact Information')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-stone-500">{t('Phone Number')}</label>
                  <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" /><Input value={draft.contact_phone} onChange={(e) => onDraft({ contact_phone: e.target.value })} placeholder={t('555-123-4567')} className="h-11 pl-10 rounded-xl bg-stone-50" /></div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-stone-500">{t('Email (Optional)')}</label>
                  <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" /><Input value={draft.contact_email} onChange={(e) => onDraft({ contact_email: e.target.value })} placeholder={t('hello@restaurant.com')} className="h-11 pl-10 rounded-xl bg-stone-50" /></div>
                </div>
              </div>
            </div>
            <button onClick={goNext} className="w-full h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: ONBOARDING_PRIMARY }} data-testid="onboarding-next-step2">
              {t('Next Step')} <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 text-center">
            <div>
              <p className="text-xs font-bold tracking-widest text-violet-500 uppercase">{t('Step 3 of 4')}</p>
              <h1 className="text-5xl font-bold mt-2 text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t('Confirm Details')}</h1>
              <p className="text-stone-500 mt-2">{t('Review your restaurant information before finalizing the setup.')}</p>
            </div>
            <div className="rounded-3xl border border-stone-200 bg-white p-6 text-left max-w-2xl mx-auto">
              {[
                { label: t('Restaurant Name'), value: draft.restaurant_name },
                { label: t('Cuisine Type'), value: [...draft.cuisine_types, draft.custom_cuisine].filter(Boolean).join(', ') || t('Not selected') },
                { label: t('Location'), value: locationString || t('Not provided') },
                { label: t('Hours'), value: `${draft.opening_time || '10:00:00'} - ${draft.closing_time || '12:00:00'}` },
                { label: t('Menu PDF'), value: draft.menu_pdf_name || t('Not uploaded') },
                { label: t('Contact'), value: `${draft.contact_phone || 'N/A'}${draft.contact_email ? ` • ${draft.contact_email}` : ''}` },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 py-3 border-b last:border-b-0 border-stone-100">
                  <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center"><CheckCircle2 className="w-4 h-4" /></div>
                  <div>
                    <p className="text-xs font-bold tracking-wider uppercase text-stone-500">{item.label}</p>
                    <p className="text-lg font-semibold text-[#1f2937]">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={goNext} className="w-full max-w-xl mx-auto h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: ONBOARDING_PRIMARY }} data-testid="onboarding-next-step3">
              {t('Save and continue')} <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 text-center">
            <div>
              <p className="text-xs font-bold tracking-widest text-violet-500 uppercase">{t('Complete')}</p>
              <div className="mx-auto w-fit h-12 w-12 rounded-2xl bg-brand-soft text-brand flex items-center justify-center"><Share2 className="w-5 h-5" /></div>
              <h1 className="text-5xl font-bold mt-3 text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t('Your QR Code is Ready')}</h1>
              <p className="text-stone-500 mt-2">{t('Turn every visit into rewards. Scan. Dine. Earn Points Instantly.')}</p>
            </div>
            <div id="onboarding-qr-card" className="mx-auto w-full max-w-sm rounded-none border border-stone-200 bg-white p-6">
              <img src={logoPreview} alt="Restaurant logo" className="h-12 w-12 rounded-xl object-cover mx-auto border border-stone-200" />
              <p className="font-bold text-2xl mt-3" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{draft.restaurant_name || t('Your Restaurant')}</p>
              <p className="text-xs text-stone-500 mt-1">{t('Get rewarded every time you eat here.')}</p>
              <div className="mt-4 inline-flex rounded-none border border-stone-200 p-3 bg-white">
                <QRCodeCanvas
                  value={storeUrl}
                  size={210}
                  level="H"
                  fgColor="#111827"
                  bgColor="white"
                />
              </div>
              <img src="/assets/flyback.png" alt="FlyBack" className="h-6 w-auto object-contain mx-auto mt-4" />
              <p className="text-[11px] text-stone-400 mt-2">{t('Powered by FlyBack')}</p>
            </div>
            <div className="mx-auto max-w-sm grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={downloadQrCard} className="h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: ONBOARDING_PRIMARY }} data-testid="onboarding-download-qr-btn">
                <Download className="w-4 h-4" /> {t('Download QR')}
              </button>
              <button onClick={shareLink} className="h-11 rounded-xl border border-stone-200 bg-white font-semibold flex items-center justify-center gap-2" data-testid="onboarding-share-link-btn">
                <Share2 className="w-4 h-4" /> {t('Share Link')}
              </button>
            </div>
            <button onClick={finish} disabled={loading} className="w-full max-w-xl mx-auto h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: ONBOARDING_PRIMARY }} data-testid="onboarding-complete-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t('Continue to Dashboard')} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </motion.div>
        )}

        {statusMessage && <p className="mt-4 text-sm text-emerald-600 text-center font-semibold">{statusMessage}</p>}
        {error && <p className="mt-2 text-sm text-red-600 text-center">{error}</p>}
      </div>
    </div>
  );
}
